import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Row, Col, Space, Grid, message, List, Tag } from 'antd';
import { DataTable, AntDColumn } from '@/components/DataTable';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, UserOutlined, TeamOutlined, BankOutlined, PercentageOutlined, CheckCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { getEmployees, deleteEmployee, updateEmployee, forceDeleteEmployee } from '@/api/employees';
import type { Employee } from '@/types/resources/employee';
import { EmployeeModal } from '@/components/features/resources/employees/EmployeeModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { EmployeesBulkUploadModal } from '@/components/features/resources/employees/EmployeesBulkUploadModal';
import { useAuth } from '@/contexts/AuthContext';
import InviteDropdown from '@/components/common/InviteDropdown';
import { getUserByDocumentId } from '@/api/users';
import { 
  inviteUser, 
  generateInviteMessage, 
  generateWhatsAppUrl, 
  generateEmailUrl, 
  generateSMSUrl 
} from '@/api/invite';
import { useSimpleCRUDTour } from '@/hooks/useSimpleCRUDTour';
import { TourButton } from '@/components/common/TourButton';

const { useBreakpoint } = Grid;
const PAGE_SIZE = 10;

export const EmployeesPage: React.FC = () => {
  const { t } = useTranslation(['employees', 'common']);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const queryClient = useQueryClient();
  const { firestoreUser, workspaceStats } = useAuth();
  const { startCRUDTour, isActive } = useSimpleCRUDTour();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setOpenModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; employeeName?: string } | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  // Queries
  const { data: employeesData = { employees: [], lastDoc: undefined, total: 0 }, isLoading, error } = useQuery({
    queryKey: ['employees', searchTerm, currentPage, pageSize, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 2] : undefined;
      
      console.log('Fetching employees:', { currentPage, pageSize, lastDoc, lastDocs: lastDocs.length });
      
      const result = await getEmployees({ 
        pageSize,
        page: currentPage,
        lastDoc,
        firestoreUser 
      });
      return result;
    },
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate employee stats from workspace stats
  const employeeStats = React.useMemo(() => {
    // Get stats from workspace stats (which come from mock data)
    const totalEmployees = workspaceStats?.totalEmployees || 0;
    const activeEmployees = Math.round(totalEmployees * 0.9); // 90% active based on mock data logic
    const activeRate = totalEmployees > 0 ? Math.round((activeEmployees / totalEmployees) * 100) : 0;
    
    const result = [
      {
        title: t('employees:stats.totalEmployees'),
        value: totalEmployees,
        icon: <TeamOutlined style={{ color: '#1890ff' }} />
      },
      {
        title: t('employees:stats.activeEmployees'),
        value: activeEmployees,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      },
      {
        title: t('employees:stats.management'),
        value: Math.round(totalEmployees * 0.2), // 20% management
        icon: <BankOutlined style={{ color: '#722ed1' }} />
      },
      {
        title: t('employees:stats.administration'),
        value: Math.round(totalEmployees * 0.25), // 25% administration
        icon: <UserOutlined style={{ color: '#13c2c2' }} />
      },
      {
        title: t('employees:stats.activeRate'),
        value: `${activeRate}%`,
        icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
      }
    ];
    
    return result;
  }, [workspaceStats, t]);

  // Stats slider functions
  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140;
      const newSlide = Math.round(scrollLeft / cardWidth);
      setCurrentSlide(newSlide);
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (employeesData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = employeesData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [employeesData.lastDoc, currentPage]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs([]);
    setCurrentPage(1);
  }, [pageSize]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchTerm, pageSize]);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteEmployee(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('employees:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setOpenDeleteDialog(false);
      setDeleteId(null);
    },
    onError: (_error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error || undefined);
      } else {
        message.error(t('employees:messages.deleteError'));
        setOpenDeleteDialog(false);
      }
    }
  });

  const forceDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      // Force delete will handle user account deletion if needed
      return forceDeleteEmployee(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('employees:messages.forceDeleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setOpenDeleteDialog(false);
      setDeleteId(null);
    },
    onError: (_error: Error) => {
      message.error(t('employees:messages.forceDeleteError'));
      setOpenDeleteDialog(false);
    }
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingEmployee(null);
    setOpenModal(true);
  }, []);

  const handleBulkUpload = useCallback(() => {
    setOpenBulkUploadDialog(true);
  }, []);

  const handleBulkUploadSuccess = useCallback(() => {
    setOpenBulkUploadDialog(false);
    queryClient.invalidateQueries({ queryKey: ['employees'] });
  }, [queryClient]);

  const handleBulkUploadCancel = useCallback(() => {
    setOpenBulkUploadDialog(false);
  }, []);

  const handleEdit = useCallback((employee: Employee) => {
    setEditingEmployee(employee);
    setOpenModal(true);
  }, []);

  const handleViewDetails = useCallback((employee: Employee) => {
    navigate(`/resources/employees/${employee.id}`);
  }, [navigate]);

  const handleDelete = useCallback((employee: Employee) => {
    setDeleteId(employee.id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId) {
      await deleteMutation.mutateAsync(deleteId);
    }
  }, [deleteId, deleteMutation]);


  const handleModalSuccess = useCallback(() => {
    setOpenModal(false);
    setEditingEmployee(null);
    queryClient.invalidateQueries({ queryKey: ['employees'] });
  }, [queryClient]);

  const handleModalCancel = useCallback(() => {
    setOpenModal(false);
    setEditingEmployee(null);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setLastDocs([]);
  }, []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // Tour handler
  const handleStartTour = useCallback(() => {
    const hasRecords = (employeesData?.employees?.length || 0) > 0;
    const recordCount = employeesData?.total || 0;
    const firstRecordId = employeesData?.employees?.[0]?.id;

    startCRUDTour({
      pageName: 'Employees Management',
      recordType: 'employee',
      hasRecords,
      recordCount,
      firstRecordId,
      onAddRecord: handleAdd,
      onViewRecord: (id: string) => {
        const employee = employeesData?.employees?.find(e => e.id === id);
        if (employee) handleViewDetails(employee);
      },
      onEditRecord: (id: string) => {
        const employee = employeesData?.employees?.find(e => e.id === id);
        if (employee) handleEdit(employee);
      },
      onDeleteRecord: (id: string) => {
        const employee = employeesData?.employees?.find(e => e.id === id);
        if (employee) handleDelete(employee);
      }
    });
  }, [startCRUDTour, employeesData, handleAdd, handleViewDetails, handleEdit, handleDelete]);

  const handleBulkDelete = useCallback((_: any, selectedRows: Employee[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete employees:', selectedRows);
  }, []);

  const handleInvite = async (employee: Employee, method: 'whatsapp' | 'email' | 'sms') => {
    if (!firestoreUser || !employee.user?.id) {
      message.error('User not authenticated or missing user reference');
      return;
    }

    try {
      // Get user details
      const user = await getUserByDocumentId(employee.user.id, firestoreUser);
      if (!user) {
        message.error('User not found');
        return;
      }

      // Validate contact information
      if (!user.email) {
        message.error('User must have an email address to be invited');
        return;
      }

      if (method === 'whatsapp' && !user.phoneNumber) {
        message.error('User must have a phone number for WhatsApp invitation');
        return;
      }

      // Invite the user (creates invitation record)
      const inviteResult = await inviteUser(user, method, firestoreUser, 'employee');
      
      if (!inviteResult.success) {
        message.error(inviteResult.message);
        return;
      }

      // Generate appropriate invite message
      const inviteMessage = generateInviteMessage(
        user,
        firestoreUser.workspace?.name || 'Unknown Workspace',
        inviteResult.inviteUrl || ''
      );

      // Open appropriate invite method
      if (method === 'whatsapp' && user.phoneNumber) {
        const whatsappUrl = generateWhatsAppUrl(user.phoneNumber, inviteMessage);
        window.open(whatsappUrl, '_blank');
      } else if (method === 'email') {
        const emailUrl = generateEmailUrl(
          user.email,
          `Invitation to join ${firestoreUser.workspace?.name || 'Unknown Workspace'}`,
          inviteMessage
        );
        window.open(emailUrl, '_blank');
      } else if (method === 'sms' && user.phoneNumber) {
        const smsUrl = generateSMSUrl(user.phoneNumber, inviteMessage);
        window.open(smsUrl, '_blank');
      }

      // Update employee invite status
      await updateEmployee(employee.id, { invited: true }, firestoreUser);
      
      // Show success message
      message.success(inviteResult.message);
      
    } catch (error) {
      console.error('Error inviting user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to invite user');
    }
  };

  // Memoized values
  const filteredEmployees = useMemo(() => {
    if (!employeesData.employees) return [];
    // In demo mode, the API already filters the data, so we just return it
    // For real mode, we might need additional client-side filtering
    return employeesData.employees;
  }, [employeesData.employees]);

  // const total = employeesData?.total ?? 0;

  const columns: AntDColumn<Employee>[] = [
    {
      title: t('employees:fields.employeeId'),
      dataIndex: 'employeeId' as keyof Employee,
      key: 'employeeId',
    },
    {
      title: t('employees:fields.user'),
      dataIndex: 'user' as keyof Employee,
      key: 'user',
      render: (_: any, record: Employee) => record.user?.displayName || record.id,
    },
    {
      title: t('employees:fields.department'),
      dataIndex: 'department' as keyof Employee,
      key: 'department',
      render: (department: string) => (
        <Tag color={department === 'Management' ? 'purple' : department === 'Administration' ? 'blue' : department === 'Maintenance' ? 'orange' : department === 'Security' ? 'red' : 'green'}>
          {t(`employees:departments.${department}`)}
        </Tag>
      ),
    },
    {
      title: t('employees:fields.role'),
      dataIndex: 'role' as keyof Employee,
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'manager' ? 'purple' : role === 'admin' ? 'blue' : role === 'maintenance' ? 'orange' : role === 'security' ? 'red' : 'green'}>
          {t(`employees:roles.${role}`)}
        </Tag>
      ),
    },
    {
      title: t('employees:fields.status'),
      dataIndex: 'status' as keyof Employee,
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {t(`employees:status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Employee) => (
        <Space className="record-actions">
          <InviteDropdown
            name={record.user?.displayName || ''}
            onInvite={(method) => handleInvite(record, method)}
          />
        </Space>
      ),
    },
  ];

  const renderEmployeeCard = (employee: Employee) => (
    <Card
      key={employee.id}
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(employee)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(employee)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(employee)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={employee.user?.displayName || employee.id}
        description={
          <Space direction="vertical" size="small">
            <div>
              {t('employees:fields.employeeId')}: {employee.employeeId}
            </div>
            <div>
              {t('employees:fields.department')}: 
              <Tag color={employee.department === 'Management' ? 'purple' : employee.department === 'Administration' ? 'blue' : employee.department === 'Maintenance' ? 'orange' : employee.department === 'Security' ? 'red' : 'green'} style={{ marginLeft: 4 }}>
                {t(`employees:departments.${employee.department}`)}
              </Tag>
            </div>
            <div>
              {t('employees:fields.role')}: 
                             <Tag color={employee.role === 'manager' ? 'purple' : employee.role === 'admin' ? 'blue' : employee.role === 'maintenance' ? 'orange' : employee.role === 'security' ? 'red' : 'green'} style={{ marginLeft: 4 }}>
                {t(`employees:roles.${employee.role}`)}
              </Tag>
            </div>
            <div>
              {t('employees:fields.status')}: 
              <Tag color={employee.status === 'active' ? 'green' : 'red'} style={{ marginLeft: 4 }}>
                {t(`employees:status.${employee.status}`)}
              </Tag>
            </div>
          </Space>
        }
      />
    </Card>
  );

  // Show error message if query fails
  useEffect(() => {
    if (error) {
      message.error(t('employees:messages.fetchError'));
    }
  }, [error, t]);

  return (
    <div style={{ width: '100%', padding: screens.md ? 24 : 16 }}>
      {/* Enhanced Stats Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: screens.xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: screens.xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: screens.xs ? 'nowrap' : 'wrap',
            justifyContent: screens.xs ? 'flex-start' : 'center'
          }}
        >
          {employeeStats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: screens.xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: screens.xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {screens.xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {employeeStats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tour Button */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <TourButton onClick={handleStartTour} disabled={isActive} size="large" />
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Input.Search
              id="employees-search-input"
              className="search-input"
              placeholder={t('common:search.placeholder')}
              allowClear
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Button
              icon={<UploadOutlined />}
              onClick={handleBulkUpload}
            >
              {t('common:actions.bulkUpload')}
            </Button>
            <Button
              id="employees-add-button"
              className="add-record-button"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('employees:actions.add')}
            </Button>
          </Space>
        </Col>
      </Row>
      {screens.xs ? (
        <List
          dataSource={filteredEmployees}
          renderItem={renderEmployeeCard}
          rowKey="id"
          loading={isLoading}
          grid={{ gutter: 16, xs: 1, sm: 1 }}
        />
      ) : (
        <Card className="data-table-card">
          <DataTable
            className="data-table"
            dataSource={filteredEmployees}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleViewDetails}
            onBulkDelete={handleBulkDelete}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: employeesData.total,
              onChange: handlePageChange,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                })
            }}
          />
        </Card>
      )}
      <EmployeeModal
        open={isModalOpen}
        employee={editingEmployee}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={() => {
          setOpenDeleteDialog(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleConfirmDelete}
        onForceDelete={() => {
          if (deleteId) {
            forceDeleteMutation.mutate(deleteId);
          }
        }}
        title={t('common:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
        isDeleting={deleteMutation.isPending || forceDeleteMutation.isPending}
      />

      <EmployeesBulkUploadModal
        visible={openBulkUploadDialog}
        onCancel={handleBulkUploadCancel}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
};